import _ from "lodash";
import React from "react";
import classNames from "classnames";
import classes from "./UsersList.scss";
import {
  getDefaultNameSortPreference,
  getDefaultStudentNameOrderPreference,
  getOrganizationId,
} from "modules/CommonSelectors";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";

import { useI18n } from "Hooks";
import { isIdEqual, prepareLoadMoreFeed } from "Utils";
import {
  getFormattedNameForUserType,
  getSubText,
} from "MessagingV2/modules/Utils";
import { graphql, compose } from "lib/apolloClient";
import { connect } from "react-redux";
import withLoader from "UIComponents/GqlWithLoader";
import Loading from "UIComponents/Loading";
import { getSearchedMembersInChatQuery } from "MessagingV2/modules/MessagingQuery";
import { getSearchedMembersInChatFromCache } from "MessagingV2/modules/MessagingGraphqlHelper";
import { useHandleScroll } from "Utils/CustomHooks";
import UserTypeCard from "../../../MemberCard";

const UserTypeList = props => {
  const {
    headerTitle = "",
    users,
    onRemoveClick,
    customAvatarContainerClassName,
    isMemberUpdate,
    defaultStudentNameOrderPreference,
    loggedInUserId,
    loggedInUserType,
    defaultStudentNameSortPreference,
    usersParams,
    searchText,
    containerRef,
  } = props;

  const { t } = useI18n(["common"]);

  const {
    networkStatus,
    loadMoreUsers = _.noop,
    hasNextPage = false,
    isFetchingMore = false,
  } = usersParams || {};

  useHandleScroll({
    networkStatus,
    loadMoreFeed: loadMoreUsers,
    hasNextPage,
    getScrollRef: () => containerRef?.current,
  });

  const userList = _.isEmpty(searchText) ? users : usersParams.users;
  const isEmptyResult =
    !_.isEmpty(searchText) && _.isEmpty(usersParams["users"]);

  const headerContainerV2 = classNames(classes.headerContainerV2, "text-label");

  if (isEmptyResult) {
    return (
      <div className={classes.container}>
        {t("common:no_user_found_for_label", {
          label: searchText,
        })}
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {!_.isEmpty(headerTitle) && !_.isEmpty(userList) ? (
        <div className={headerContainerV2}>{headerTitle}</div>
      ) : null}
      {_.map(userList, user => {
        const isCurrentLoggedInUser = isIdEqual(loggedInUserId, user.id);

        const { id, profileImage } = user;

        let name = getFormattedNameForUserType({
          user,
          orderBy: defaultStudentNameSortPreference,
          isFormattedNameV2: true,
          defaultStudentNameOrderPreference,
        });

        if (isCurrentLoggedInUser) {
          name = t("common:you_with_full_name", { fullName: name });
        }
        return (
          <UserTypeCard
            key={id}
            onRemoveClick={() => onRemoveClick({ user })}
            customAvatarContainerClassName={customAvatarContainerClassName}
            avatarId={id}
            mainText={name}
            subText={getSubText({
              loggedInUserRole: loggedInUserType,
              messagedUserObj: user,
              messagedUserRole: _.get(user, "type"),
              t,
              defaultStudentNameSortPreference,
            })}
            profileImage={profileImage}
            isRemovable={isMemberUpdate && !isCurrentLoggedInUser}
          />
        );
      })}
      {isFetchingMore ? <Loading /> : null}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const defaultStudentNameSortPreference = getDefaultNameSortPreference(state);
  const defaultStudentNameOrderPreference = getDefaultStudentNameOrderPreference(
    state
  );
  const { user_type: loggedInUserType, id: loggedInUserId } = getUserInfo();

  return {
    defaultStudentNameSortPreference,
    defaultStudentNameOrderPreference,
    loggedInUserType,
    loggedInUserId,
    isData: true,
    isLoading: false,
  };
};

const UsersList = compose(
  connect(mapStateToProps),
  graphql(getSearchedMembersInChatQuery, {
    name: "getSearchedMembersInChat",
    skip: ({ searchText }) => _.isEmpty(searchText),
    options: props => {
      const { searchText, chatId } = props;
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          organizationId: getOrganizationId(),
          chatId: chatId,
          input: {
            first: 10,
            searchText,
          },
        },
      };
    },
    props: ({ getSearchedMembersInChat, ownProps }) => {
      const queryVariables = _.get(getSearchedMembersInChat, "variables", {});

      const cachedData = getSearchedMembersInChatFromCache(queryVariables);

      const users = _.map(
        _.get(cachedData, "usersV2.edges", []),
        user => user?.node,
        []
      );
      const hasNextPage = _.get(
        cachedData,
        "usersV2.pageInfo.hasNextPage",
        false
      );

      const usersParams = {
        users,
        hasNextPage,
        isFetchingMore: getSearchedMembersInChat.networkStatus === 3,
        loadMoreUsers: prepareLoadMoreFeed({
          variables: {
            ...queryVariables,
            input: {
              ...queryVariables.input,
              first: 10,
              before: _.get(cachedData, "usersV2.pageInfo.endCursor"),
            },
          },
          queryData: getSearchedMembersInChat,
          key: "usersV2",
        }),
      };

      return {
        isLoading: _.includes(
          [1, 2],
          getSearchedMembersInChat["networkStatus"]
        ),
        isData: !_.isEmpty(cachedData),
        usersParams,
      };
    },
  }),
  withLoader
)(UserTypeList);

export default UsersList;
